"use client"

/**
 * Every Leo surface the app shell mounts, and the rules for which one shows.
 *
 * Leo has grown three shells — the docked panel, the floating window, and the
 * corner FAB — and the choice between them depends on the persisted `dock`
 * preference, whether the window is minimised, the route, and whether the
 * viewport can carry a draggable window at all. That policy used to sit inline
 * in `App.tsx`, which meant the app shell imported four Leo modules and two
 * viewport hooks to answer a question that is entirely Leo's business, and any
 * other host (the generated starter, a consumer app) had to copy the same
 * fifteen lines correctly to get the same behaviour.
 *
 * The shells themselves stay lazy: the panel and the window are only fetched
 * once someone opens Leo, and the FAB is small enough to ship eagerly because
 * it is the thing that opens him. Leo appearance mounts as a **sibling** of
 * Ask Leo (not inside its toolbar tree) so portal pointer events cannot bubble
 * through React and drag both windows together.
 *
 * Window mode must not reflow the hub: it portals as a fixed overlay. Only the
 * docked panel claims flex width and may collapse the primary sidebar. The app
 * row's `data-ask-leo-open` hook follows `useAskLeoDockedPanelOpen`, which lives
 * in `hooks/use-ask-leo-docked-panel-open.ts` so this module can stay a Fast
 * Refresh boundary.
 *
 * @see components/ask-leo-context.tsx — `open`, `dock`, `minimized`
 * @see docs/ask-leo-pattern.md — view modes and their entry points
 */

import * as React from "react"
import { useLocation } from "react-router"

import { useAskLeo } from "@/components/ask-leo-context"
import { useLeoAmbience } from "@/components/leo-ambience-context"
import { LeoLauncherFab } from "@/components/leo-launcher-fab"
import { useSecondaryPanel } from "@/components/sidebar"
import { useSidebar } from "@/components/ui/sidebar"
import { useIsMobile } from "@/hooks/use-mobile"
import { useSidebarReflowZoom } from "@/hooks/use-sidebar-reflow-zoom"
import { isProductsHomePath } from "@/lib/product-home"

const AskLeoSidebar = React.lazy(() =>
  import("@/components/ask-leo-sidebar").then(m => ({
    default: m.AskLeoSidebar,
  })),
)

const AskLeoWindow = React.lazy(() =>
  import("@/components/ask-leo-window").then(m => ({
    default: m.AskLeoWindow,
  })),
)

const LeoAmbienceWindow = React.lazy(() =>
  import("@/components/leo-ambience-window").then(m => ({
    default: m.LeoAmbienceWindow,
  })),
)

/**
 * Docked Leo can collapse the primary rail to free width. Closing Leo must
 * not force the rail open — that overwrites SidebarAutoCollapse on compose
 * and the user's icon-rail preference. Persist is off so this never writes
 * `sidebar_state_v2`.
 */
function AskLeoPrimarySidebarCollapse({ panelOpen }: { panelOpen: boolean }) {
  const { setOpen: setSidebarOpen } = useSidebar()
  const prevPanelOpen = React.useRef(panelOpen)
  React.useEffect(() => {
    if (panelOpen && !prevPanelOpen.current) {
      setSidebarOpen(false, { persist: false })
    }
    prevPanelOpen.current = panelOpen
  }, [panelOpen, setSidebarOpen])
  return null
}

/**
 * One floating layer at a time on mobile / reflow: opening Ask Leo closes the
 * secondary scope sheet. Desktop docked Leo hides secondary in {@link SecondaryPanel}.
 */
function AskLeoSecondaryPanelCollapse({ panelOpen }: { panelOpen: boolean }) {
  const { activePanel, closeSecondaryFlyout, secondaryFlyoutVisible } =
    useSecondaryPanel()
  const isMobile = useIsMobile()
  const reflowZoom = useSidebarReflowZoom()
  const navFlyout = isMobile || reflowZoom
  const prevOpen = React.useRef(panelOpen)

  React.useEffect(() => {
    if (
      panelOpen &&
      !prevOpen.current &&
      navFlyout &&
      activePanel &&
      secondaryFlyoutVisible
    ) {
      closeSecondaryFlyout()
    }
    prevOpen.current = panelOpen
  }, [
    panelOpen,
    activePanel,
    navFlyout,
    secondaryFlyoutVisible,
    closeSecondaryFlyout,
  ])

  return null
}

export function AskLeoShell() {
  return (
    <>
      <AskLeoDockedSlot />
      <AskLeoFloatingSlot />
    </>
  )
}

/**
 * Docked Ask Leo rail — `ResizableHandle` + `ResizablePanel` for the workspace
 * group. Overlay flyout mounts from {@link AskLeoFloatingSlot}.
 */
export function AskLeoDockedSlot() {
  const { open, dock, minimized } = useAskLeo()
  const isMobile = useIsMobile()
  const reflowZoom = useSidebarReflowZoom()
  const windowMode = dock === "window" && !isMobile && !reflowZoom
  const dockedPanelOpen = open && !windowMode
  const overlayFlyout = dockedPanelOpen && (isMobile || reflowZoom)

  if (!dockedPanelOpen || overlayFlyout) return null

  return (
    <React.Suspense fallback={null}>
      <AskLeoSidebar />
    </React.Suspense>
  )
}

/** Window / FAB / ambience / overlay flyout — mount outside `ResizablePanelGroup`. */
export function AskLeoFloatingSlot() {
  const { pathname } = useLocation()
  const { open, dock, minimized } = useAskLeo()
  const { settingsOpen } = useLeoAmbience()
  const isMobile = useIsMobile()
  const reflowZoom = useSidebarReflowZoom()

  const windowMode = dock === "window" && !isMobile && !reflowZoom
  const dockedPanelOpen = open && !windowMode
  const overlayFlyout = dockedPanelOpen && (isMobile || reflowZoom)

  const salesFraming = isProductsHomePath(pathname)
  const showFab = salesFraming || (windowMode && minimized)

  return (
    <>
      <AskLeoPrimarySidebarCollapse panelOpen={dockedPanelOpen && !overlayFlyout} />
      <AskLeoSecondaryPanelCollapse panelOpen={dockedPanelOpen} />
      {dockedPanelOpen && overlayFlyout ? (
        <React.Suspense fallback={null}>
          <AskLeoSidebar />
        </React.Suspense>
      ) : null}
      {open && windowMode ? (
        <React.Suspense fallback={null}>
          <AskLeoWindow />
        </React.Suspense>
      ) : null}
      {settingsOpen ? (
        <React.Suspense fallback={null}>
          <LeoAmbienceWindow />
        </React.Suspense>
      ) : null}
      {showFab ? <LeoLauncherFab salesFraming={salesFraming} /> : null}
    </>
  )
}

/** @deprecated Prefer {@link AskLeoDockedSlot} + {@link AskLeoFloatingSlot}. */
export function AskLeoShellLegacy() {
  return (
    <>
      <AskLeoDockedSlot />
      <AskLeoFloatingSlot />
    </>
  )
}
